import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  ChevronRight as ChevronRightIcon,
  ContentCopy as CopyIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { JsonViewer } from '../JsonViewer';
import { CodeHighlight } from '../CodeHighlight';
import {
  JsonSchema,
  describeConstraints,
  describeType,
  exampleFromSchema,
  listSchema,
  resolveSchema,
  schemaToPydantic,
} from '../../utils/jsonSchema';

export type SchemaViewMode = 'structure' | 'example' | 'schema' | 'python';

const VIEW_LABELS: Record<SchemaViewMode, string> = {
  structure: 'Structure',
  example: 'Example',
  schema: 'JSON Schema',
  python: 'Python',
};

export interface SchemaViewProps {
  schema: JsonSchema | null | undefined;
  title?: React.ReactNode;
  /** The output is a list of `schema` items. */
  isList?: boolean;
  /** Item name used for list descriptions and the Python class name, e.g. "contact". */
  name?: string;
  /** Short note under the header, e.g. where the schema came from. */
  caption?: React.ReactNode;
  loading?: boolean;
  /** Python source to show instead of the code generated from the schema. */
  python?: string;
  views?: SchemaViewMode[];
  defaultView?: SchemaViewMode;
  maxHeight?: number | string;
  /** Levels expanded initially (0 = only top-level fields visible). */
  defaultExpandDepth?: number;
  emptyMessage?: React.ReactNode;
}

const MAX_TREE_DEPTH = 16;

interface ChildEntry {
  key: string;
  label: string;
  node: unknown;
  required: boolean;
  refPath: string[];
}

const isRecord = (value: unknown): value is JsonSchema =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/** Rows nested under a node: object properties, or the properties of an array's items. */
function childrenOf(schema: JsonSchema, root: JsonSchema, refPath: string[]): ChildEntry[] {
  const out: ChildEntry[] = [];
  const types: string[] = Array.isArray(schema.type) ? schema.type : schema.type ? [schema.type] : [];

  if (isRecord(schema.properties)) {
    const required = new Set<string>(Array.isArray(schema.required) ? schema.required : []);
    Object.entries(schema.properties).forEach(([key, node]) => {
      out.push({ key, label: key, node, required: required.has(key), refPath });
    });
  }
  if (isRecord(schema.additionalProperties)) {
    out.push({ key: '[key]', label: '[key: string]', node: schema.additionalProperties, required: false, refPath });
  }
  if (types.includes('array') || (!types.length && schema.items !== undefined)) {
    const items = Array.isArray(schema.items) ? schema.items[0] : schema.items;
    if (items !== undefined) {
      const resolved = resolveSchema(items, root, refPath);
      const info = describeType(items, root, refPath);
      if (!resolved.recursive && info.kind === 'object') {
        return [...out, ...childrenOf(resolved.schema, root, resolved.refPath)];
      }
      if (info.kind === 'array' || info.kind === 'union') {
        out.push({ key: '[]', label: '[item]', node: items, required: true, refPath });
      }
    }
  }
  const variants = schema.anyOf || schema.oneOf;
  if (Array.isArray(variants) && variants.length > 1) {
    variants.forEach((variant: unknown, i: number) => {
      const kind = describeType(variant, root, refPath).kind;
      if (kind === 'object' || kind === 'array') {
        out.push({ key: `|${i}`, label: `option ${i + 1}`, node: variant, required: false, refPath });
      }
    });
  }
  return out;
}

const formatEnumValue = (v: unknown) => (typeof v === 'string' ? `"${v}"` : JSON.stringify(v));

interface TreeContext {
  root: JsonSchema;
  isExpanded: (path: string, depth: number) => boolean;
  toggle: (path: string, expanded: boolean) => void;
}

const TypeBadge: React.FC<{ label: string; tone: 'object' | 'array' | 'enum' | 'scalar' | 'warn'; title?: string }> = ({
  label,
  tone,
  title,
}) => {
  const theme = useTheme();
  const color = {
    object: theme.palette.primary.main,
    array: theme.palette.secondary.main,
    enum: theme.palette.warning.dark,
    scalar: theme.palette.text.secondary,
    warn: theme.palette.error.main,
  }[tone];
  const badge = (
    <Box
      component="span"
      sx={{
        fontFamily: 'monospace',
        fontSize: '0.68rem',
        lineHeight: 1.6,
        px: 0.6,
        borderRadius: 0.75,
        color,
        bgcolor: alpha(color, 0.08),
        border: `1px solid ${alpha(color, 0.25)}`,
        whiteSpace: 'nowrap',
        flexShrink: 0,
      }}
    >
      {label}
    </Box>
  );
  return title ? <Tooltip title={title}>{badge}</Tooltip> : badge;
};

const SchemaRow: React.FC<{
  entry: ChildEntry;
  path: string;
  depth: number;
  ctx: TreeContext;
}> = ({ entry, path, depth, ctx }) => {
  const resolved = resolveSchema(entry.node, ctx.root, entry.refPath);
  const { schema } = resolved;
  const info = describeType(entry.node, ctx.root, entry.refPath);
  const tooDeep = depth >= MAX_TREE_DEPTH;
  const children = resolved.recursive || tooDeep ? [] : childrenOf(schema, ctx.root, resolved.refPath);
  const expandable = children.length > 0;
  const expanded = expandable && ctx.isExpanded(path, depth);
  const description: string | undefined = schema.description || schema.title;
  const constraints = describeConstraints(schema);

  let badge: React.ReactNode;
  if (resolved.recursive) {
    badge = <TypeBadge label={`recursive: ${resolved.refName}`} tone="warn" title="Refers back to an enclosing object" />;
  } else if (info.kind === 'enum' || info.kind === 'const') {
    const values = info.enumValues || [];
    const shown = values.slice(0, 4).map(formatEnumValue).join(' | ');
    const more = values.length > 4 ? ` +${values.length - 4}` : '';
    badge = (
      <TypeBadge
        label={`${info.label} ${shown}${more}`}
        tone="enum"
        title={values.length > 4 ? values.map(formatEnumValue).join(', ') : undefined}
      />
    );
  } else {
    const tone = info.kind === 'object' ? 'object' : info.kind === 'array' ? 'array' : 'scalar';
    const typeTitle = schema.format ? `${schema.type}, format: ${schema.format}` : undefined;
    badge = <TypeBadge label={info.label} tone={tone} title={typeTitle} />;
  }

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          pl: depth * 1.75,
          pr: 0.5,
          py: 0.25,
          borderRadius: 0.5,
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <Box sx={{ width: 20, flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
          {expandable && (
            <IconButton
              size="small"
              onClick={() => ctx.toggle(path, expanded)}
              sx={{ p: 0, mt: '1px' }}
              aria-label={expanded ? `Collapse ${entry.label}` : `Expand ${entry.label}`}
            >
              <ChevronRightIcon
                sx={{ fontSize: 16, transition: 'transform 0.15s', transform: expanded ? 'rotate(90deg)' : 'none' }}
              />
            </IconButton>
          )}
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', rowGap: 0.25 }}>
            <Box
              component="span"
              onClick={expandable ? () => ctx.toggle(path, expanded) : undefined}
              sx={{
                fontFamily: 'monospace',
                fontSize: '0.78rem',
                fontWeight: 600,
                cursor: expandable ? 'pointer' : 'default',
                wordBreak: 'break-all',
              }}
            >
              {entry.label}
              {entry.required ? (
                <Tooltip title="Required">
                  <Box component="span" sx={{ color: 'error.main', ml: 0.25 }}>*</Box>
                </Tooltip>
              ) : null}
            </Box>
            {badge}
            {resolved.refName && !resolved.recursive && info.kind === 'object' && (
              <Typography component="span" sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>
                {resolved.refName}
              </Typography>
            )}
            {resolved.nullable && (
              <Typography component="span" sx={{ fontSize: '0.68rem', color: 'text.secondary', fontStyle: 'italic' }}>
                nullable
              </Typography>
            )}
            {constraints.length > 0 && (
              <Typography component="span" sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>
                {constraints.join(' · ')}
              </Typography>
            )}
          </Box>
          {description && (
            <Tooltip title={description} placement="bottom-start" enterDelay={400}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: 1.4 }}
              >
                {description}
              </Typography>
            </Tooltip>
          )}
          {tooDeep && !resolved.recursive && (
            <Typography variant="caption" color="text.disabled" sx={{ display: 'block' }}>
              Nested too deeply to display
            </Typography>
          )}
        </Box>
      </Box>
      {expanded && (
        <Box sx={{ position: 'relative' }}>
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              bottom: 4,
              left: `calc(${depth * 14}px + 10px)`,
              borderLeft: '1px dashed',
              borderColor: 'divider',
            }}
          />
          {children.map((child) => (
            <SchemaRow key={child.key} entry={child} path={`${path}/${child.key}`} depth={depth + 1} ctx={ctx} />
          ))}
        </Box>
      )}
    </>
  );
};

export const SchemaView: React.FC<SchemaViewProps> = ({
  schema,
  title,
  isList = false,
  name,
  caption,
  loading = false,
  python,
  views = ['structure', 'example', 'schema', 'python'],
  defaultView = 'structure',
  maxHeight = 360,
  defaultExpandDepth = 1,
  emptyMessage = 'No schema defined',
}) => {
  const built = useMemo<JsonSchema | null>(() => {
    if (!schema) return null;
    return isList ? listSchema(schema, name) : schema;
  }, [schema, isList, name]);
  // Callers often build the schema during render; key on content so expand state survives.
  const schemaKey = useMemo(() => (built ? JSON.stringify(built) : ''), [built]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const effective = useMemo(() => built, [schemaKey]);

  const [view, setView] = useState<SchemaViewMode>(views.includes(defaultView) ? defaultView : views[0]);
  const [mode, setMode] = useState<'default' | 'all' | 'none'>('default');
  const [overrides, setOverrides] = useState<Record<string, boolean>>({});
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setOverrides({});
    setMode('default');
  }, [effective]);

  const example = useMemo(() => (effective && view === 'example' ? exampleFromSchema(effective) : undefined), [effective, view]);
  const pythonSource = useMemo(() => {
    if (view !== 'python') return '';
    if (python) return python;
    return effective ? schemaToPydantic(effective, name || 'response') : '';
  }, [effective, view, python, name]);

  const copyText = (): string => {
    if (!effective) return '';
    if (view === 'example') return JSON.stringify(example, null, 2);
    if (view === 'python') return pythonSource;
    return JSON.stringify(effective, null, 2);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(copyText()).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }).catch(() => undefined);
  };

  const ctx: TreeContext | null = effective
    ? {
        root: effective,
        isExpanded: (path, depth) =>
          path in overrides ? overrides[path] : mode === 'all' ? true : mode === 'none' ? false : depth < defaultExpandDepth,
        toggle: (path, expanded) => setOverrides((prev) => ({ ...prev, [path]: !expanded })),
      }
    : null;

  const renderStructure = () => {
    if (!effective || !ctx) return null;
    const rootResolved = resolveSchema(effective, effective, []);
    const rootInfo = describeType(effective, effective, []);
    const rows = childrenOf(rootResolved.schema, effective, rootResolved.refPath);
    let summary: string;
    if (rootInfo.kind === 'array') {
      const items = rootResolved.schema.items;
      const itemResolved = resolveSchema(items, effective, rootResolved.refPath);
      const itemLabel = itemResolved.schema.title || itemResolved.refName || name || describeType(items, effective).label;
      summary = rows.length > 0 ? `List of ${itemLabel} · each item has ${rows.length} field${rows.length === 1 ? '' : 's'}` : `List of ${rootInfo.label.replace(/^array<|>$/g, '')}`;
    } else if (rootInfo.kind === 'object') {
      summary = `Object · ${rows.length} field${rows.length === 1 ? '' : 's'}`;
    } else {
      summary = `Single value · ${rootInfo.label}`;
    }
    const hasNesting = rows.some((r) => childrenOf(resolveSchema(r.node, effective, r.refPath).schema, effective, r.refPath).length > 0);

    return (
      <>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, minHeight: 24 }}>
          <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
            {summary}
            {rows.some((r) => r.required) && (
              <>
                {' · '}
                <Box component="span" sx={{ color: 'error.main' }}>*</Box> required
              </>
            )}
          </Typography>
          {hasNesting && (
            <>
              <Button size="small" sx={{ minWidth: 0, px: 0.75, py: 0, fontSize: '0.7rem', textTransform: 'none' }} onClick={() => { setOverrides({}); setMode('all'); }}>
                Expand all
              </Button>
              <Button size="small" sx={{ minWidth: 0, px: 0.75, py: 0, fontSize: '0.7rem', textTransform: 'none' }} onClick={() => { setOverrides({}); setMode('none'); }}>
                Collapse all
              </Button>
            </>
          )}
        </Box>
        <Box sx={{ maxHeight, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1, py: 0.5, bgcolor: 'background.paper' }}>
          {rows.length > 0 ? (
            rows.map((row) => <SchemaRow key={row.key} entry={row} path={row.key} depth={0} ctx={ctx} />)
          ) : rootInfo.kind === 'object' || rootInfo.label === 'array<object>' ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 1.5, py: 0.5 }}>
              No fields defined (any JSON object is accepted)
            </Typography>
          ) : (
            <SchemaRow entry={{ key: '$', label: '(value)', node: effective, required: true, refPath: [] }} path="$" depth={0} ctx={ctx} />
          )}
        </Box>
      </>
    );
  };

  const jsonSx = {
    '& > div': { maxHeight, p: 1.25 },
    '& div pre': { fontSize: '0.75rem' },
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.75 }}>
        {title && (
          <Typography variant="subtitle2" sx={{ flex: '1 1 auto' }}>
            {title}
          </Typography>
        )}
        {!title && <Box sx={{ flex: '1 1 auto' }} />}
        {loading && <CircularProgress size={14} />}
        {effective && views.length > 1 && (
          <ToggleButtonGroup
            size="small"
            exclusive
            value={view}
            onChange={(_, next: SchemaViewMode | null) => next && setView(next)}
            sx={{ '& .MuiToggleButton-root': { py: 0.125, px: 0.875, fontSize: '0.7rem', textTransform: 'none', lineHeight: 1.6 } }}
          >
            {views.map((v) => (
              <ToggleButton key={v} value={v}>
                {VIEW_LABELS[v]}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        )}
        {effective && view !== 'structure' && (
          <Tooltip title={copied ? 'Copied' : `Copy ${VIEW_LABELS[view]}`}>
            <IconButton size="small" onClick={handleCopy} sx={{ p: 0.5 }}>
              {copied ? <CheckIcon sx={{ fontSize: 16 }} /> : <CopyIcon sx={{ fontSize: 16 }} />}
            </IconButton>
          </Tooltip>
        )}
      </Box>
      {caption && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
          {caption}
        </Typography>
      )}
      {!effective ? (
        loading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
            <CircularProgress size={14} />
            <Typography variant="caption" color="text.secondary">Loading schema...</Typography>
          </Box>
        ) : (
          <Typography variant="caption" color="text.secondary">{emptyMessage}</Typography>
        )
      ) : view === 'structure' ? (
        renderStructure()
      ) : view === 'example' ? (
        <Box sx={jsonSx}>
          <JsonViewer data={example} />
        </Box>
      ) : view === 'schema' ? (
        <Box sx={jsonSx}>
          <JsonViewer data={effective} />
        </Box>
      ) : (
        <CodeHighlight code={pythonSource} language="python" maxHeight={maxHeight} fontSize="0.75rem" />
      )}
    </Box>
  );
};

export default SchemaView;
